import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Card from '@src/components/Card';
import { COMMISSION_PAYMENT_LIMIT } from '@src/constants/affiliate';
import {
  CreditCardOutlined,
  ReportGmailerrorredOutlined,
} from '@mui/icons-material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { AFFILIATE_POLICY_LINK } from '@src/configs';
import { Button } from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import i18n from '@src/languages';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import Withdrawal from '@src/containers/Withdrawal';
import { WITHDRAWAL_SOURCE } from '@src/constants/withdrawal';
import CancelWithdrawalRequestDialog from '@src/containers/EarningsManagement/TransactionsHistory/CancelWithdrawalRequestDialog';
import RequestDialogSuccess from '@src/containers/EarningsManagement/RequestDialogSuccess';
import { CommissionBoxStyled } from './index.style';
import CreatePaymentDrawer from './PaymentRequestDrawer';

// The Withdrawal dialog expects the voice-revenue shape: it reads `amount`,
// while affiliate transactions use `money` (`id` is already present from the API).
const toWithdrawalShape = (txn) => (txn ? { ...txn, amount: txn.money } : null);

const CommissionBox = ({
  currentCommission,
  pendingCommission,
  receiveCommission,
  onReloadCommission,
  onViewRequests,
}) => {
  const { t } = useTranslation();

  const { language } = i18n;

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const isPaymentRequestV2Enabled = getFeatureValue(
    FEATURE_KEYS.AFFILIATE_PAYMENT_REQUEST_V2,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const [openPaymentRequestDrawer, setOpenPaymentRequestDrawer] =
    useState(false);
  const [processingTransaction, setProcessingTransaction] = useState(null);
  const [paidTransaction, setPaidTransaction] = useState(null);
  const [isOpenCancelWithdrawal, setIsOpenCancelWithdrawal] = useState(false);
  const [loadingCancelWithdrawal, setLoadingCancelWithdrawal] = useState(false);
  const [isOpenRequestSuccess, setIsOpenRequestSuccess] = useState(false);

  // Affiliate commission is recognised up to the end of N-5 (N = today).
  const commissionDataUpdatedDate = moment()
    .subtract(5, 'days')
    .format('DD/MM/YYYY');

  const fetchProcessingTransaction = async () => {
    const response =
      await apis.affiliateTransaction.getInProcessingAffiliateTransaction();
    if (response?.status) {
      setProcessingTransaction(
        toWithdrawalShape(response.result?.inProcessingAffiliateTransaction),
      );
    }
  };

  const fetchPaidTransaction = async () => {
    const response =
      await apis.affiliateTransaction.getPaidAffiliateTransaction();
    if (response?.status) {
      setPaidTransaction(
        toWithdrawalShape(response.result?.affiliateTransactions?.[0]),
      );
    }
  };

  useEffect(() => {
    if (!isPaymentRequestV2Enabled) return;
    fetchProcessingTransaction();
    fetchPaidTransaction();
  }, [isPaymentRequestV2Enabled]);

  const handleOpenPaymentRequestDrawer = () => {
    setOpenPaymentRequestDrawer(true);
  };

  const handleClosePaymentRequestDrawer = () =>
    setOpenPaymentRequestDrawer(false);

  const handleCloseWithdrawalDialog = (_, reason) => {
    if (reason === 'backdropClick') return;
    setOpenPaymentRequestDrawer(false);
  };

  const handleSuccessPaymentRequest = () => {
    setIsOpenRequestSuccess(true);
    onReloadCommission();
    fetchProcessingTransaction();
    fetchPaidTransaction();
  };

  const notify = (message, severity) =>
    dispatch(actions.noti.push({ severity, message }));

  const handleCancelWithdrawalRequest = async () => {
    if (!processingTransaction?.id) return;
    setLoadingCancelWithdrawal(true);
    const response = await apis.affiliateTransaction.cancelAffiliateTransaction(
      processingTransaction.id,
    );
    if (response?.status) {
      notify(t('cancelWithdrawalRequestSuccess'), 'success');
    } else {
      notify(t('cancellationWithdrawalRequestFailed'), 'error');
    }
    setLoadingCancelWithdrawal(false);
    setIsOpenCancelWithdrawal(false);
    setOpenPaymentRequestDrawer(false);
    onReloadCommission();
    fetchProcessingTransaction();
  };

  return (
    <CommissionBoxStyled>
      <Card className="commission-box">
        <div className="commission">
          <div
            className="title"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            {t('amountOfCommissionReceived')}
            {isPaymentRequestV2Enabled && (
              <Tooltip
                arrow
                placement="top"
                title={t('commissionDataUpdatedUntil', {
                  date: commissionDataUpdatedDate,
                })}
              >
                <InfoRoundedIcon
                  sx={{ color: COLOR.indigoIcon, fontSize: '16px' }}
                />
              </Tooltip>
            )}
          </div>
          <div className="current-commission">
            {currentCommission.toLocaleString(language)} <span>đ</span>
          </div>
          <div className="break-line" />
          <div className="">
            <div className="title">{t('amountInStandby')}</div>
            <div className="current-commission">
              {pendingCommission.toLocaleString(language)} <span>đ</span>
            </div>
          </div>
          <div className="">
            <div className="title">{t('amountReceived')}</div>
            <div className="current-commission">
              {receiveCommission.toLocaleString(language)} <span>đ</span>
            </div>
          </div>
          <div className="break-line" />
          <Button
            align="center"
            variant="contained"
            size="small"
            disabled={
              !isPaymentRequestV2Enabled &&
              currentCommission < COMMISSION_PAYMENT_LIMIT
            }
            startIcon={<CreditCardOutlined />}
            onClick={handleOpenPaymentRequestDrawer}
          >
            {processingTransaction
              ? t('viewPendingPaymentRequest')
              : t('sendPaymentRequest')}
          </Button>
        </div>
        <div className="note">
          <ReportGmailerrorredOutlined />
          <div
            className="text-note"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: isPaymentRequestV2Enabled
                ? t('affiliatePolicyDetailV2', {
                    paymentLimit:
                      COMMISSION_PAYMENT_LIMIT.toLocaleString(language),
                  })
                : t('affiliatePolicyDetail', {
                    affiliatePolicyLink: AFFILIATE_POLICY_LINK,
                    paymentLimit:
                      COMMISSION_PAYMENT_LIMIT.toLocaleString(language),
                  }),
            }}
          />
        </div>
        {isPaymentRequestV2Enabled ? (
          <>
            <Withdrawal
              isOpenPopupWithdrawal={openPaymentRequestDrawer}
              closePopupWithdrawal={handleCloseWithdrawalDialog}
              balance={currentCommission}
              onSuccess={handleSuccessPaymentRequest}
              source={WITHDRAWAL_SOURCE.AFFILIATE}
              paidTransaction={paidTransaction}
              processingWithdrawal={processingTransaction}
              isShowDetail={Boolean(processingTransaction)}
              onCancelWithdrawal={() => setIsOpenCancelWithdrawal(true)}
              key={openPaymentRequestDrawer}
            />
            <CancelWithdrawalRequestDialog
              open={isOpenCancelWithdrawal}
              onClose={() => setIsOpenCancelWithdrawal(false)}
              withdrawalRequestId={processingTransaction?.id}
              onCancelWithdrawalRequest={handleCancelWithdrawalRequest}
              loading={loadingCancelWithdrawal}
            />
            <RequestDialogSuccess
              open={isOpenRequestSuccess}
              onClose={() => setIsOpenRequestSuccess(false)}
              onViewHistory={onViewRequests}
            />
          </>
        ) : (
          <CreatePaymentDrawer
            open={openPaymentRequestDrawer}
            currentCommission={currentCommission}
            onReloadCommission={onReloadCommission}
            onClose={handleClosePaymentRequestDrawer}
          />
        )}
      </Card>
    </CommissionBoxStyled>
  );
};

export default CommissionBox;
